import { logger } from '@adornis/base/logging.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { ImageFile } from '@adornis/file-utils/db/images.js';
import { getLLMRouter } from '../../llm/llm-gate.js';
import {
  grammarCoordinateAgentDefaultTemperature,
  grammarCoordinateAgentModel,
  grammarCoordinateAgentRetryTemperature,
} from '../../../../shared/models.js';
import { grammarCoordinateAgentSystemPrompt } from '../../../prompts.js';
import { imagesToBase64URLsWithSizeFallback } from '../../../utils/transform-file.js';

/** Percentage (0–100) bounding box for a grammar error underline. */
export interface GrammarErrorBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Input to the coordinate agent: the erroneous text + a context snippet to find it on the page. */
export interface GrammarErrorLocateInput {
  index: number;
  text: string;
  context: string;
}

export interface GrammarErrorLocation {
  index: number;
  pageNumber: number;
  bbox: GrammarErrorBox | null;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isValidBox(value: any): value is GrammarErrorBox {
  return (
    !!value && isFiniteNumber(value.x) && isFiniteNumber(value.y) && isFiniteNumber(value.w) && isFiniteNumber(value.h)
  );
}

// The agent must return one entry per input error index. We accept null bboxes (error not located)
// but the overall response must be parseable JSON with an `errors` array.
function parseLocations(rawAnswer: string, expectedCount: number): GrammarErrorLocation[] | null {
  try {
    const parsed = JSON.parse(rawAnswer);
    const errors = Array.isArray(parsed?.errors) ? parsed.errors : null;
    if (!errors) return null;

    const byIndex = new Map<number, GrammarErrorLocation>();
    for (const entry of errors) {
      const index = Number(entry?.index);
      if (!Number.isInteger(index)) continue;
      const pageNumber = isFiniteNumber(entry?.pageNumber) ? entry.pageNumber : 1;
      const bbox = isValidBox(entry?.bbox) ? entry.bbox : null;
      byIndex.set(index, { index, pageNumber, bbox });
    }

    // Fill any missing indices with a null location so callers always get one entry per error.
    const result: GrammarErrorLocation[] = [];
    for (let i = 0; i < expectedCount; i++) {
      result.push(byIndex.get(i) ?? { index: i, pageNumber: 1, bbox: null });
    }
    return result;
  } catch {
    return null;
  }
}

/**
 * Locate each grammar error on the student submission image(s). Returns one entry per input error
 * (in index order); `bbox` is null when the error could not be located. Never throws — grammar
 * results are display-only, so a failure degrades to "errors listed without underline".
 *
 * Coordinates are percentages (0–100) of the page.
 */
export async function runGrammarCoordinateAgent(
  imageIds: string[],
  errors: GrammarErrorLocateInput[],
): Promise<{ locations: GrammarErrorLocation[]; cost: number }> {
  const emptyResult = (): GrammarErrorLocation[] => errors.map(e => ({ index: e.index, pageNumber: 1, bbox: null }));

  try {
    if (!imageIds?.length || !errors.length) {
      return { locations: emptyResult(), cost: 0 };
    }

    const images: ImageFile[] = [];
    for (const imageId of imageIds) {
      if (!imageId) continue;
      const image = await getByID(ImageFile, imageId)(ImageFile.allFields);
      if (image) images.push(image as ImageFile);
      else logger.info({ imageId, context: 'GRAMMAR_BBX' }, 'Image not found');
    }

    if (images.length === 0) {
      logger.warn({ context: 'GRAMMAR_BBX' }, 'No images found for provided IDs');
      return { locations: emptyResult(), cost: 0 };
    }

    const imageBase64urls = await imagesToBase64URLsWithSizeFallback(images, { logTag: '[GRAMMAR_BBX]' });

    const errorListText = errors.map(e => `- index ${e.index}: text="${e.text}" | context="${e.context}"`).join('\n');

    const contentParts: any[] = [
      {
        type: 'text',
        text: `These are the ${imageBase64urls.length} page(s) of the student's answer (in order). Locate each of the following grammar/spelling errors on the page(s) and return its bounding box as percentages (0–100):\n${errorListText}`,
      },
      ...imageBase64urls.map(url => ({ type: 'image_url', image_url: { url, detail: 'high' } })),
    ];

    const systemPrompt = grammarCoordinateAgentSystemPrompt();

    let cost = 0;
    const maxRetries = 1;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      const router = getLLMRouter();
      const temperature =
        attempt > 0 ? grammarCoordinateAgentRetryTemperature : grammarCoordinateAgentDefaultTemperature;
      try {
        const response = await router.chat.completions.create({
          model: grammarCoordinateAgentModel,
          provider: 'openrouter',
          temperature,
          messages: [
            { role: 'system', content: systemPrompt },
            { role: 'user', content: contentParts },
          ],
          response_format: { type: 'json_object' },
        });

        cost += Number((response.usage as any)?.cost ?? 0);

        const rawContent = response.choices?.[0]?.message?.content;
        const rawAnswer = typeof rawContent === 'string' ? rawContent : JSON.stringify(rawContent ?? '');
        const locations = parseLocations(rawAnswer, errors.length);
        if (locations) {
          logger.info(
            { attempt: attempt + 1, located: locations.filter(l => l.bbox).length, context: 'GRAMMAR_BBX' },
            'Grammar coordinate agent succeeded',
          );
          return { locations, cost };
        }
        logger.warn({ attempt: attempt + 1, context: 'GRAMMAR_BBX' }, 'Grammar coordinate agent: unparseable response');
      } catch (error) {
        logger.error(
          { err: error, attempt: attempt + 1, context: 'GRAMMAR_BBX' },
          'Grammar coordinate agent call failed',
        );
      }
    }

    // All attempts failed → degrade gracefully.
    return { locations: emptyResult(), cost };
  } catch (err) {
    logger.error({ err, context: 'GRAMMAR_BBX' }, 'Grammar coordinate agent failed unexpectedly');
    return { locations: emptyResult(), cost: 0 };
  }
}
